import { DateTime } from "luxon";
import type { WorkingHoursTimeSpanDto, WorkingHoursWeekValueDto } from "@me-pos/schedule-modal";
import { BranchWorkScheduleInput, BranchWorkScheduleProxy } from "@me-team/host/main/graphql/types";

export const WORK_SCHEDULE_TYPE_GENERAL = "general";
export const WORK_SCHEDULE_TYPE_KITCHEN = "kitchen";

export const emptyTimeSpan: WorkingHoursTimeSpanDto = {
    startTime: null,
    endTime: null,
};

type BackendWorkScheduleItem = Pick<BranchWorkScheduleProxy, "day" | "period">;

export type TWorkScheduleType = typeof WORK_SCHEDULE_TYPE_GENERAL | typeof WORK_SCHEDULE_TYPE_KITCHEN;

export const hasAnyWorkingHours = (value: WorkingHoursWeekValueDto | null): boolean => {
    if (!Array.isArray(value)) return false;

    return value.some((day) => {
        if (!day.isEnabled) return false;

        return (day.timeSpans ?? []).some((span) => Boolean(span.startTime) && Boolean(span.endTime));
    });
};

export const normalizeWeekValue = (value: WorkingHoursWeekValueDto): WorkingHoursWeekValueDto => {
    return value.map((day) => {
        if (!day.isEnabled) {
            return {
                ...day,
                timeSpans: [{ startTime: null, endTime: null }],
            };
        }

        const validSpans = (day.timeSpans ?? []).filter((span) => Boolean(span.startTime) && Boolean(span.endTime));

        return {
            ...day,
            timeSpans: validSpans.length ? validSpans : [{ startTime: null, endTime: null }],
        };
    });
};

// DTO (Formik) to an array of objects for mutation
export const mapWeekValueToWorkSchedulesInput = (
    value: WorkingHoursWeekValueDto | null,
    type: TWorkScheduleType
): BranchWorkScheduleInput[] => {
    if (!value) return [];

    const normalizedValue = normalizeWeekValue(value);

    const result: BranchWorkScheduleInput[] = [];

    normalizedValue.forEach((day) => {
        if (!day.isEnabled) return;

        day.timeSpans.forEach((span) => {
            if (!span.startTime || !span.endTime) return;

            result.push({
                type,
                day: day.dayNumber,
                startTime: span.startTime,
                endTime: span.endTime,
            });
        });
    });

    return result;
};

// collect data from the backend in WeekValueDto
export const mapBackendSchedulesToWeekValue = (schedules: BackendWorkScheduleItem[]): WorkingHoursWeekValueDto => {
    const week: WorkingHoursWeekValueDto = Array.from({ length: 7 }, (_, dayNumber) => ({
        dayNumber,
        isEnabled: false,
        timeSpans: [emptyTimeSpan],
    }));

    schedules.forEach((item) => {
        const dayNumber = item.day?.number;
        const period = item.period;

        if (typeof dayNumber !== "number") return;
        if (dayNumber < 0 || dayNumber > 6) return;
        if (!period) return;

        const parts = period.split("-").map((part) => part.trim());
        if (parts.length < 2) return;

        const startTime = parts[0] || null;
        const endTime = parts[1] || null;

        if (!startTime || !endTime) return;

        const day = week[dayNumber];
        if (!day) return;

        day.isEnabled = true;

        const hasOnlyEmpty = day.timeSpans.length === 1 && !day.timeSpans[0].startTime && !day.timeSpans[0].endTime;

        if (hasOnlyEmpty) {
            day.timeSpans = [{ startTime, endTime }];
        } else {
            day.timeSpans.push({ startTime, endTime });
        }
    });

    return week;
};

export type WorkingHoursSummaryItem = {
    rangeLabel: string;
    periods: string[];
};

export const formatWeekValueSummaryItems = (
    value: WorkingHoursWeekValueDto | null,
    locale: string,
    timeZone: string
): WorkingHoursSummaryItem[] => {
    if (!value || !hasAnyWorkingHours(value)) return [];

    const normalizedValue = normalizeWeekValue(value);

    const weekStart = DateTime.now().setZone(timeZone).setLocale(locale).startOf("week").startOf("day");

    const getDayShort = (dayNumber: number) => {
        const label = weekStart.plus({ days: dayNumber }).toFormat("ccc");
        return label.charAt(0).toUpperCase() + label.slice(1);
    };

    // for each day we save a list of intervals
    const dayPeriodsList = normalizedValue.map((day): string[] | null => {
        if (!day.isEnabled) return null;

        const periods = (day.timeSpans ?? [])
            .filter((span) => span.startTime && span.endTime)
            .map((span) => `${span.startTime}-${span.endTime}`);

        return periods.length ? periods : null;
    });

    //  key for grouping days with equal intervals
    const dayKeyList = dayPeriodsList.map((periods) => (periods ? periods.join("|") : null));

    type Segment = { startDay: number; endDay: number; key: string; periods: string[] };
    const segments: Segment[] = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const key = dayKeyList[dayIndex];
        const periods = dayPeriodsList[dayIndex];

        if (!key || !periods) continue;

        const prev = segments[segments.length - 1];

        if (prev && prev.key === key && prev.endDay === dayIndex - 1) {
            prev.endDay = dayIndex;
        } else {
            segments.push({ startDay: dayIndex, endDay: dayIndex, key, periods });
        }
    }

    const formatRange = (startDay: number, endDay: number) => {
        if (startDay === endDay) return getDayShort(startDay);
        return `${getDayShort(startDay)}-${getDayShort(endDay)}`;
    };

    return segments.map((seg) => ({
        rangeLabel: formatRange(seg.startDay, seg.endDay),
        periods: seg.periods,
    }));
};
